import type {
  Theme,
  ThemeMode,
  ThemeName,
} from 'foundations-theming-base/types';
import { MODERN_THEMES } from 'foundations-theming-base/modern-themes';
import { getHostname } from './utils';
import { safeLocalStorageGet } from './safeLocalStorage';

const PRIVATE_TOOLS_REGEXP =
  /^(tools|private)(?:-([a-z]+[0-9]))?.hubteam(qa)?\.com/i;

export const THEME_DATA_ATTRIBUTE = 'data-hubspot-theme';
export const THEME_DATA_TAG = 'hubspotTheme';

export const LOCAL_STORAGE_THEME_KEY = 'trellis:theme';
export const LOCAL_STORAGE_THEME_VERSION_KEY = 'trellis:theme-version';
export const LOCAL_STORAGE_THEME_OVERRIDES_KEY = 'trellis:theme-overrides';

export type ChosenTheme = { themeName: ThemeName; themeMode: ThemeMode };
export type ThemeVersion =
  | {
      type: 'deployed';
      env?: 'qa' | 'prod';
      channel: 'stable' | 'canary';
    }
  | {
      type: 'version';
      version: string;
      channel: 'stable' | 'canary';
    }
  | { type: 'local'; channel: 'stable' | 'canary' }
  | { type: 'default' };

export type ThemeSettings = {
  themeName: ThemeName;
  themeMode: ThemeMode;
  themeVersion: ThemeVersion;
  themeOverrides: Partial<Theme>;
};

export const DEFAULT_SETTINGS: ThemeSettings = {
  themeName: 'canvas',
  themeMode: 'light',
  themeVersion: {
    type: 'default',
  },
  themeOverrides: {},
};

export function getIsTrellisEnabledDefault(): boolean {
  try {
    // private/tools subdomain pages should not read theme from localStorage since not tokenized
    const hostname = getHostname();
    const isStorybook = (window as any).HS_STORYBOOK !== undefined;
    const isThemableApp = !hostname.match(PRIVATE_TOOLS_REGEXP) || isStorybook;

    return isThemableApp;
  } catch {
    // do nothing
  }
  return true;
}

let isTrellisEnabled = getIsTrellisEnabledDefault();

export function setTrellisEnabledFlag(value: boolean) {
  isTrellisEnabled = value;
}

function shouldReadLocalThemeSettings() {
  return isTrellisEnabled;
}

function getThemeLocalStorageSetting(key: string) {
  if (!shouldReadLocalThemeSettings()) {
    return undefined;
  }

  return safeLocalStorageGet(key);
}

function splitChosenTheme(theme: string): ChosenTheme | undefined {
  if (Object.prototype.hasOwnProperty.call(MODERN_THEMES, theme)) {
    const { themeName, themeMode } =
      MODERN_THEMES[theme as keyof typeof MODERN_THEMES];
    return { themeName, themeMode };
  }
  const splitAt = theme.lastIndexOf('-');
  const themeName = theme.slice(0, splitAt);
  const themeMode = theme.slice(splitAt + 1);
  if (splitAt < 1 || !themeMode) {
    return undefined;
  }
  return { themeName, themeMode } as ChosenTheme;
}

function validateThemeVersion(themeVersion: any): themeVersion is ThemeVersion {
  if (typeof themeVersion !== 'object' || !themeVersion.type) {
    return false;
  }

  if (themeVersion.type === 'deployed') {
    return (
      typeof themeVersion.channel === 'string' &&
      (!themeVersion.env || typeof themeVersion.env === 'string')
    );
  }

  if (themeVersion.type === 'version') {
    return (
      typeof themeVersion.version === 'string' &&
      typeof themeVersion.channel === 'string'
    );
  }

  if (themeVersion.type === 'local') {
    return typeof themeVersion.channel === 'string';
  }

  if (themeVersion.type === 'default') {
    return true;
  }

  return false;
}

function getThemeInfoFromHtml(): ChosenTheme | undefined {
  try {
    // dataset converts kebab to camel case
    const dataset = document.documentElement.dataset;
    if (!dataset) {
      return undefined;
    }

    const themeSetting = dataset[THEME_DATA_TAG];
    if (!themeSetting) {
      return undefined;
    }

    const modernTheme = Object.values(MODERN_THEMES).find(
      ({ themeName, themeMode, legacyFallback }) =>
        dataset.hubspotThemeName === themeName &&
        dataset.hubspotThemeMode === themeMode &&
        themeSetting === legacyFallback
    );
    if (modernTheme) {
      return {
        themeName: modernTheme.themeName,
        themeMode: modernTheme.themeMode,
      };
    }

    return splitChosenTheme(themeSetting);
  } catch {
    return undefined;
  }
}

function getChosenTheme(): ChosenTheme {
  const localThemeName = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_KEY);
  if (localThemeName) {
    const splitTheme = splitChosenTheme(localThemeName);
    if (splitTheme) {
      return splitTheme;
    }
  }

  const themeInfoFromHtml = getThemeInfoFromHtml();
  if (themeInfoFromHtml) {
    return themeInfoFromHtml;
  }

  return {
    themeName: DEFAULT_SETTINGS.themeName,
    themeMode: DEFAULT_SETTINGS.themeMode,
  };
}

function getThemeVersion(): ThemeVersion {
  const localThemeVersion = getThemeLocalStorageSetting(
    LOCAL_STORAGE_THEME_VERSION_KEY
  );

  if (localThemeVersion) {
    try {
      const themeVersion = JSON.parse(localThemeVersion);
      if (validateThemeVersion(themeVersion)) {
        return themeVersion;
      }
    } catch {
      // do nothing
    }
  }

  return DEFAULT_SETTINGS.themeVersion;
}

function getThemeOverrides() {
  const localThemeSettings = getThemeLocalStorageSetting(
    LOCAL_STORAGE_THEME_OVERRIDES_KEY
  );
  if (localThemeSettings) {
    try {
      const themeOverrides = JSON.parse(localThemeSettings);
      if (typeof themeOverrides === 'object') {
        return themeOverrides;
      }
    } catch {
      // do nothing
    }
  }

  return {};
}

export function getLocalThemeSettings(): ThemeSettings {
  const chosenTheme = getChosenTheme();
  const themeVersion = getThemeVersion();
  const themeOverrides = getThemeOverrides();

  return {
    themeOverrides,
    themeVersion,
    ...chosenTheme,
  };
}
